import { useEffect, useMemo, useRef } from 'react'

import { fromUnixTime, isFuture } from 'date-fns'
import { differenceInMilliseconds } from 'date-fns/esm'

import useStore from '@/store/useStore'

import { UpdatedSliderData } from './useUpdateSliderData'

export const useGetNearestEventTime = (sliderData: UpdatedSliderData[]) =>
  useMemo(() => {
    const nearestEventsTime = sliderData
      .filter(({ data }) => {
        const endTime = data?.event?.endTime
        return endTime && isFuture(fromUnixTime(endTime))
      })
      .map(({ data }) => {
        const startTime = data?.event?.startTime
        const endTime = data?.event?.endTime
        const isFutureEvent = startTime && isFuture(fromUnixTime(startTime))
        return isFutureEvent ? startTime : (endTime as number)
      })
    return nearestEventsTime.length ? Math.min(...nearestEventsTime) : null
  }, [sliderData])

export const useRefreshSliderDataByNearestEventTime = (
  sliderData: UpdatedSliderData[],
  updateSliderData: () => void,
) => {
  const isFirstRender = useRef(true)
  const isAppHidden = useStore((store) => store.common.isAppHidden)
  const nearestEventTime = useGetNearestEventTime(sliderData)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
    } else {
      !isAppHidden && updateSliderData()
    }
  }, [isAppHidden])

  useEffect(() => {
    if (!nearestEventTime) {
      return
    }

    const nearestTime = fromUnixTime(nearestEventTime)
    const remainingTime = differenceInMilliseconds(nearestTime, new Date())
    const timerId = setTimeout(updateSliderData, remainingTime)

    return () => {
      clearTimeout(timerId)
    }
  }, [updateSliderData, nearestEventTime, isAppHidden])
}
